import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle, Download, Printer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sid}`);
      setPaymentData(res.data);

      if (res.data.payment_status === "paid") {
        setStatus("success");
        // Fetch receipt
        try {
          const receiptRes = await axios.get(`${API}/payments/receipt/${sid}`);
          setReceipt(receiptRes.data);
        } catch {}
        return;
      } else if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus("error");
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-[#141414] flex items-center justify-center px-6 py-12">
      <div className="max-w-lg w-full" data-testid="payment-success-page">
        {status === "checking" && (
          <div className="glass-panel p-10 text-center">
            <Loader2 className="w-16 h-16 text-[#FF0033] mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
            <p className="text-white/70">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "success" && (
          <div id="receipt-content">
            {/* Receipt Header */}
            <div className="glass-panel p-8 mb-1 rounded-b-none text-center" data-testid="receipt-header">
              <CheckCircle className="w-12 h-12 text-[#39FF14] mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-1" data-testid="payment-success-title">Payment Successful</h2>
              <p className="text-white/50 text-sm">Thank you for your payment</p>
            </div>

            {/* Receipt Body */}
            <div className="glass-panel p-8 mb-1 rounded-none" data-testid="receipt-body">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#FF0033] font-bold">The Humble Hires</p>
                  <p className="text-xs text-white/50">Premium Speaker Rental</p>
                </div>
                {receipt && (
                  <div className="text-right">
                    <p className="text-xs text-white/50">Receipt #{receipt.receipt_id}</p>
                    <p className="text-xs text-white/50">{formatDate(receipt.date)}</p>
                  </div>
                )}
              </div>

              {receipt && (
                <>
                  {/* Customer Info */}
                  <div className="border-t border-white/10 pt-4 mb-4">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Customer</p>
                    <p className="text-sm font-medium">{receipt.customer_name}</p>
                    {receipt.customer_email && <p className="text-xs text-white/50">{receipt.customer_email}</p>}
                    {receipt.customer_phone && <p className="text-xs text-white/50">{receipt.customer_phone}</p>}
                  </div>

                  {/* Item Details */}
                  <div className="border-t border-white/10 pt-4 mb-4">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-3">
                      {receipt.type === "booking" ? "Booking Details" : "Purchase Details"}
                    </p>
                    <p className="text-sm font-bold mb-2">{receipt.item_name}</p>

                    {receipt.event_date && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Event Date</span>
                        <span>{formatDate(receipt.event_date)}</span>
                      </div>
                    )}
                    {receipt.duration && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Duration</span>
                        <span>{receipt.duration} {receipt.duration === 1 ? "day" : "days"}</span>
                      </div>
                    )}
                    {receipt.delivery_address && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Delivery</span>
                        <span className="text-right max-w-[200px] text-xs">{receipt.delivery_address}</span>
                      </div>
                    )}

                    {/* Bundle items breakdown */}
                    {receipt.items && receipt.items.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {receipt.items.map((item, i) => (
                          <div key={i} className="flex justify-between text-xs text-white/50">
                            <span>{item.quantity}x {item.name} {item.days ? `(${item.days}d)` : ""}</span>
                            <span>£{item.item_total?.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Price Breakdown */}
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Payment Summary</p>
                    {receipt.subtotal && receipt.subtotal !== receipt.amount && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Subtotal</span>
                        <span>£{receipt.subtotal?.toFixed(2)}</span>
                      </div>
                    )}
                    {receipt.discount_amount > 0 && (
                      <div className="flex justify-between text-sm mb-1 text-[#39FF14]">
                        <span>Discount ({receipt.discount_code})</span>
                        <span>-£{receipt.discount_amount?.toFixed(2)}</span>
                      </div>
                    )}
                    {receipt.delivery_fee > 0 && (
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-white/50">Delivery Fee</span>
                        <span>£{receipt.delivery_fee?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/50">Payment Method</span>
                      <span>{receipt.payment_method}</span>
                    </div>
                    <div className="flex justify-between items-center pt-3 mt-3 border-t border-white/10">
                      <span className="font-bold">Total Paid</span>
                      <span className="text-2xl font-black text-[#FF0033]">£{receipt.amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}

              {!receipt && (
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-bold">Total Paid</span>
                    <span className="text-2xl font-black text-[#FF0033]">
                      £{paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : "0.00"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Receipt Footer */}
            <div className="glass-panel p-6 rounded-t-none text-center" data-testid="receipt-footer">
              <p className="text-xs text-white/40 mb-4">
                {paymentData?.metadata?.type === "music_pack"
                  ? "You'll receive your download link via email shortly."
                  : "Your booking has been confirmed. Check your email for details."}
              </p>
              <div className="flex gap-3">
                <Button onClick={handlePrint} variant="outline" className="flex-1 border-white/20 hover:border-white/40 font-bold" data-testid="print-receipt">
                  <Printer className="w-4 h-4 mr-2" />Print Receipt
                </Button>
                <Button onClick={() => navigate("/")} className="flex-1 bg-[#FF0033] text-white hover:bg-[#FF0033]/80 font-bold" data-testid="back-home-btn">
                  Back to Home
                </Button>
              </div>
              <p className="text-[10px] text-white/30 mt-4">The Humble Hires — thehumblehires@gmail.com</p>
            </div>
          </div>
        )}

        {(status === "error" || status === "timeout" || status === "expired") && (
          <div className="glass-panel p-10 text-center">
            <XCircle className="w-16 h-16 text-[#FF0033] mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Payment Issue</h2>
            <p className="text-white/70 mb-6">
              {status === "timeout" ? "Payment is still processing. Check your email for confirmation." :
               status === "expired" ? "Payment session expired. Please try again." :
               "Something went wrong. Please contact us if you were charged."}
            </p>
            <Button onClick={() => navigate("/")} className="bg-white/10 text-white hover:bg-white/20 font-bold w-full h-12">
              Back to Home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
